import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { LoadingOverlay } from '@mantine/core';
import { authClient } from '../../lib/auth-client';
import { useBetterAuthUserStore } from '../../store/betterAuthUserStore';

interface BetterAuthProtectedRouteProps {
  children?: React.ReactNode;
  component?: React.ComponentType<any>;
  permission?: string | string[];
}

export default function BetterAuthProtectedRoute({
  children,
  component: Component,
  permission,
}: BetterAuthProtectedRouteProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const {
    userData,
    fetchUserData,
    setIsAuthenticated: setStoreAuth,
    clearUserData,
  } = useBetterAuthUserStore();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if user has a valid session using better-auth
        const session = await authClient.getSession();

        if (session.data?.session && session.data?.user) {
          // User is authenticated via cookie
          setIsAuthenticated(true);
          setStoreAuth(true);

          // Fetch user data if not already available
          if (!userData) {
            try {
              const betterAuthUser = {
                id: session.data.user.id,
                email: session.data.user.email,
                name: session.data.user.name,
                emailVerified: session.data.user.emailVerified,
              };
              await fetchUserData(betterAuthUser);
            } catch (dataError) {
              console.error('❌ Error fetching user data:', dataError);
              // Don't clear auth if just data fetching fails
            }
          }
        } else {
          // No valid session
          setIsAuthenticated(false);
          setStoreAuth(false);
          clearUserData();
        }
      } catch (error) {
        console.error('❌ Error checking authentication:', error);
        setIsAuthenticated(false);
        setStoreAuth(false);
        clearUserData();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once

  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Check permissions if provided
  if (permission && userData?.me) {
    const userPermissions = userData.me.permissions || [];
    const requiredPermissions = Array.isArray(permission)
      ? permission
      : [permission];

    const hasPermission = requiredPermissions.some((perm) =>
      userPermissions.some((userPerm: any) => userPerm.name === perm)
    );

    if (!hasPermission) {
      return <Navigate to="/" />;
    }
  }

  if (Component) {
    return <Component />;
  }

  return <>{children}</>;
}
